import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Info, Loader2, Plus, ShieldOff, X } from 'lucide-react';
import {
  adminBlinding,
  studyStaff,
  studies,
  type BlindingListRow,
} from '@/api/client';
import { humanRole } from '@/lib/humanise';
import { fmtDate } from '@/lib/dates';
import { PageHeader } from '@/components/layout/PageHeader';
import { ConfirmModal } from '@/components/layout/ConfirmModal';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { studyLabel } from '@/lib/studyLabel';

/**
 * One table holds both designations (f88e1b9): 'unblinded_staff' rows are
 * people explicitly UNBLINDED on the study; anything else is blinded. The page
 * used to label every open row "Still blinded" from active_to alone, so
 * unblinded staff read as blinded (13 Oct 2026).
 */
function isUnblindedRow(r: Pick<BlindingListRow, 'status'>): boolean {
  return r.status === 'unblinded_staff';
}

const ROLE_HINTS = [
  { value: '', label: 'Not specified' },
  { value: 'blinded_monitor', label: 'Blinded monitor' },
  { value: 'blinded_assessor', label: 'Blinded assessor' },
  { value: 'blinded_investigator', label: 'Blinded investigator' },
  { value: 'blinded_sponsor', label: 'Blinded sponsor' },
];

export function BlindingPage() {
  const qc = useQueryClient();
  const [studyFilter, setStudyFilter] = useState<string>('');
  const [assigning, setAssigning] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [ending, setEnding] = useState<BlindingListRow | null>(null);

  const studiesQ = useQuery({
    queryKey: ['studies', 'list'],
    queryFn: () => studies.list(),
    staleTime: 5 * 60_000,
  });

  const listQ = useQuery({
    queryKey: ['adminBlinding', 'list', studyFilter || 'all'],
    queryFn: () =>
      adminBlinding.list(studyFilter ? { studyId: studyFilter } : undefined),
    staleTime: 30_000,
  });

  const endM = useMutation({
    mutationFn: (id: string) => adminBlinding.update(id, { end_now: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['adminBlinding'] });
      toast.success(ending && isUnblindedRow(ending) ? 'Unblinded designation ended.' : 'Blinding ended.');
      setEnding(null);
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'End failed'));
    },
  });

  const rows = listQ.data?.blindings ?? [];
  const activeRows = rows.filter((r) => r.active);
  const historicalRows = rows.filter((r) => !r.active);
  const studyRows = studiesQ.data?.rows ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/admin"
        title="Blinding assignments"
        subtitle="Per-(user, study) blinding across every study. Blinded users see redaction notices instead of study content. Ending a blinding stamps active_to; the row survives forever for the audit trail."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEmergency(true)}
              title="Lift blinding for one person until a set time — the only way to unblind an Investigator"
            >
              Emergency unblind
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setAssigning(true)}
            >
              <Plus className="h-4 w-4" /> Assign blinding
            </button>
          </div>
        }
      />

      <div className="card p-4">
        <label className="label text-xs">Filter by study</label>
        <select
          className="input max-w-2xl text-sm"
          value={studyFilter}
          onChange={(e) => setStudyFilter(e.target.value)}
        >
          <option value="">— All studies —</option>
          {studyRows
            .slice()
            .sort((a, b) =>
              (a.protocol_number || a.title).localeCompare(b.protocol_number || b.title),
            )
            .map((s) => (
              <option key={s.study_id} value={s.study_id}>
                {studyLabel(s)}
              </option>
            ))}
        </select>
      </div>

      {listQ.isLoading && (
        <div className="card flex items-center justify-center gap-2 p-8 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading blindings…
        </div>
      )}

      {listQ.isError && (
        <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(listQ.error)}`}>
          {isPermissionError(listQ.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{errorText(listQ.error, 'Failed to load blindings.')}</span>
        </div>
      )}

      {listQ.data && (
        <>
          <BlindingSection
            title={`Active (${activeRows.length})`}
            rows={activeRows}
            emptyText={studyFilter ? 'No active blindings on this study.' : 'No active blindings on any study.'}
            onEnd={(row) => setEnding(row)}
            showStudy={!studyFilter}
          />
          {historicalRows.length > 0 && (
            <BlindingSection
              title={`History (${historicalRows.length})`}
              rows={historicalRows}
              emptyText=""
              onEnd={null}
              showStudy={!studyFilter}
            />
          )}
        </>
      )}

      {emergency && (
        <EmergencyUnblindDialog
          studyOptions={studyRows}
          defaultStudyId={studyFilter}
          onClose={() => setEmergency(false)}
          onSuccess={() => {
            setEmergency(false);
            qc.invalidateQueries({ queryKey: ['adminBlinding'] });
          }}
        />
      )}

      {assigning && (
        <AssignDialog
          studyOptions={studyRows}
          defaultStudyId={studyFilter}
          onClose={() => setAssigning(false)}
          onSuccess={() => {
            setAssigning(false);
            qc.invalidateQueries({ queryKey: ['adminBlinding'] });
          }}
        />
      )}

      {ending && (
        <ConfirmModal
          title={isUnblindedRow(ending) ? 'End unblinded designation' : 'End blinding'}
          body={
            isUnblindedRow(ending) ? (
              <p>
                End the unblinded designation for{' '}
                <b>{ending.user_name ?? ending.user_email ?? ending.user_id}</b>? Row stays in the
                database for audit; from then on they are no longer treated as unblinded staff on
                this study.
              </p>
            ) : (
              <p>
                End the blinding for <b>{ending.user_name ?? ending.user_email ?? ending.user_id}</b>?
                Row stays in the database for audit; the user immediately regains access to
                study content once this saves.
              </p>
            )
          }
          confirmLabel={
            endM.isPending ? 'Ending…' : isUnblindedRow(ending) ? 'End designation' : 'End blinding'
          }
          isPending={endM.isPending}
          onCancel={() => setEnding(null)}
          onConfirm={() => endM.mutate(ending.blinding_id)}
        />
      )}
    </div>
  );
}

function BlindingSection({
  title,
  rows,
  emptyText,
  onEnd,
  showStudy,
}: {
  title: string;
  rows: BlindingListRow[];
  emptyText: string;
  onEnd: ((row: BlindingListRow) => void) | null;
  showStudy: boolean;
}) {
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-surface-border bg-surface-muted/50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-ink-muted">{emptyText}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-surface-muted/30 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 font-medium">User</th>
              {showStudy && <th className="px-3 py-2 font-medium">Study</th>}
              <th className="px-3 py-2 font-medium">Designation</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-3 py-2 font-medium">Active from</th>
              <th className="px-3 py-2 font-medium">Ended</th>
              <th className="px-3 py-2 font-medium">Reason</th>
              {onEnd && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.blinding_id} className="border-b border-surface-border last:border-0">
                <td className="px-3 py-2">
                  <div className="text-ink-primary">{r.user_name ?? '—'}</div>
                  <div className="text-[11px] text-ink-muted">{r.user_email ?? ''}</div>
                </td>
                {showStudy && (
                  <td className="px-3 py-2 text-xs text-ink-secondary">
                    <div className="text-ink-primary">{r.study_title ?? r.study_protocol_number ?? '—'}</div>
                    {r.study_protocol_number && r.study_title && (
                      <div className="font-mono text-[11px] text-ink-muted">{r.study_protocol_number}</div>
                    )}
                  </td>
                )}
                <td className="px-3 py-2 text-xs">
                  {isUnblindedRow(r) ? (
                    <span className="badge badge-success">Unblinded</span>
                  ) : (
                    <span className="badge badge-warning">Blinded</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-ink-secondary">
                  {r.blinded_role_master_ref ? humanRole(r.blinded_role_master_ref) : '—'}
                </td>
                <td className="px-3 py-2 text-xs text-ink-secondary">{fmtDate(r.active_from)}</td>
                <td className="px-3 py-2 text-xs text-ink-secondary">
                  {r.active_to ? fmtDate(r.active_to) : 'Ongoing'}
                </td>
                <td className="px-3 py-2 text-xs text-ink-muted">{r.reason ?? '—'}</td>
                {onEnd && (
                  <td className="px-3 py-2 text-right">
                    {r.active && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded border border-surface-border px-2 py-1 text-xs text-ink-primary hover:bg-surface-muted/50"
                        onClick={() => onEnd(r)}
                        title={isUnblindedRow(r) ? 'End this unblinded designation' : 'End this blinding'}
                      >
                        <ShieldOff className="h-3 w-3" /> End
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function AssignDialog({
  studyOptions,
  defaultStudyId,
  onClose,
  onSuccess,
}: {
  studyOptions: Array<{ study_id: string; protocol_number: string; title: string }>;
  defaultStudyId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [studyId, setStudyId] = useState(defaultStudyId);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');

  const staffQ = useQuery({
    queryKey: ['study', studyId, 'staff'],
    queryFn: () => studyStaff.list(studyId),
    enabled: !!studyId,
    staleTime: 60_000,
  });

  const assignM = useMutation({
    mutationFn: () =>
      adminBlinding.assign({
        user_id: userId,
        study_id: studyId,
        blinded_role_master_ref: role || undefined,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success('Blinding assigned.');
      onSuccess();
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Assign failed'));
    },
  });

  const users = useMemo(
    () =>
      (staffQ.data?.assignments ?? [])
        .slice()
        .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? '')),
    [staffQ.data],
  );

  const sortedStudies = useMemo(
    () =>
      studyOptions
        .slice()
        .sort((a, b) =>
          (a.protocol_number || a.title).localeCompare(b.protocol_number || b.title),
        ),
    [studyOptions],
  );

  const canSubmit = !!studyId && !!userId && !assignM.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg border border-surface-border bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          <h2 className="text-base font-semibold text-ink-primary">Assign blinding</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <label className="label text-xs">Study <span className="text-danger-600">*</span></label>
            <select
              className="input text-sm"
              value={studyId}
              onChange={(e) => {
                setStudyId(e.target.value);
                setUserId('');
              }}
            >
              <option value="">— Select a study —</option>
              {sortedStudies.map((s) => (
                <option key={s.study_id} value={s.study_id}>
                  {studyLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">User to blind <span className="text-danger-600">*</span></label>
            <select
              className="input text-sm"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={!studyId || staffQ.isLoading}
            >
              <option value="">
                {!studyId
                  ? '— Pick a study first —'
                  : staffQ.isLoading
                    ? '— Loading study team —'
                    : users.length === 0
                      ? '— No staff assigned to this study yet —'
                      : '— Select a user —'}
              </option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name || u.email} · {humanRole(u.role)}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-ink-muted">
              Only users assigned to this study's staff roster can be blinded. Add someone
              to the study first via Studies → Staff.
            </p>
          </div>
          <div>
            <label className="label text-xs">Blinded role (optional)</label>
            <select
              className="input text-sm"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLE_HINTS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-ink-muted">
              Free-form label for the audit trail; does not change what's redacted.
            </p>
          </div>
          <div>
            <label className="label text-xs">Reason (optional)</label>
            <textarea
              className="input text-sm"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={4000}
              placeholder="e.g. Blinded to treatment allocation for endpoint adjudication."
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-3">
          <button
            type="button"
            className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
            onClick={onClose}
            disabled={assignM.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded bg-accent-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-60"
            onClick={() => assignM.mutate()}
            disabled={!canSubmit}
          >
            {assignM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Feedback (2026-09-29) — "PI/SI should be blinded always. Emergency change
 * to unblinded with time limit."
 *
 * This is the ONLY route to an unblinded Investigator: the Assign Staff
 * dialog and the DoA grid both refuse it outright now. The expiry is an
 * explicit datetime the admin picks rather than a fixed window, per the
 * client's ask, and `active_to` is honoured by the read predicate so nothing
 * has to expire it on a schedule. Revoking early is the existing End action
 * on the row.
 */
function EmergencyUnblindDialog({
  studyOptions,
  defaultStudyId,
  onClose,
  onSuccess,
}: {
  studyOptions: Array<{ study_id: string; protocol_number: string; title: string }>;
  defaultStudyId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [studyId, setStudyId] = useState(defaultStudyId);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  // datetime-local wants 'YYYY-MM-DDTHH:mm' in the viewer's own zone.
  const [activeTo, setActiveTo] = useState('');

  const staffQ = useQuery({
    queryKey: ['study', studyId, 'staff'],
    queryFn: () => studyStaff.list(studyId),
    enabled: !!studyId,
    staleTime: 60_000,
  });

  const grantM = useMutation({
    mutationFn: () =>
      adminBlinding.emergencyUnblind({
        user_id: userId,
        study_id: studyId,
        reason: reason.trim(),
        active_to: new Date(activeTo).toISOString(),
      }),
    onSuccess: (r) => {
      toast.success(`Unblinded until ${new Date(r.expires_at).toLocaleString()}.`);
      onSuccess();
    },
    onError: (err: unknown) => {
      toast.error(errorText(err, 'Emergency unblinding failed'));
    },
  });

  const users = useMemo(
    () =>
      (staffQ.data?.assignments ?? [])
        .slice()
        .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? '')),
    [staffQ.data],
  );

  const sortedStudies = useMemo(
    () =>
      studyOptions
        .slice()
        .sort((a, b) =>
          (a.protocol_number || a.title).localeCompare(b.protocol_number || b.title),
        ),
    [studyOptions],
  );

  const expiryInFuture = !!activeTo && new Date(activeTo).getTime() > Date.now();
  const canSubmit =
    !!studyId && !!userId && reason.trim().length >= 10 && expiryInFuture && !grantM.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-lg border border-surface-border bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-4">
          <h2 className="text-base font-semibold text-danger-700">Emergency unblinding</h2>
          <button type="button" onClick={onClose} className="text-ink-muted hover:text-ink-primary">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            This reveals treatment allocation to the person you pick, until the time you set.
            It is recorded against your name in the audit trail. Investigators must otherwise
            stay blinded, so this is the only way to lift it.
          </div>
          <div>
            <label className="label text-xs">Study <span className="text-danger-600">*</span></label>
            <select
              className="input text-sm"
              value={studyId}
              onChange={(e) => {
                setStudyId(e.target.value);
                setUserId('');
              }}
            >
              <option value="">— Select a study —</option>
              {sortedStudies.map((s) => (
                <option key={s.study_id} value={s.study_id}>
                  {studyLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">Person to unblind <span className="text-danger-600">*</span></label>
            <select
              className="input text-sm"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={!studyId || staffQ.isLoading}
            >
              <option value="">
                {!studyId ? '— Pick a study first —' : staffQ.isLoading ? '— Loading study team —' : '— Select a person —'}
              </option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.full_name || u.email} · {humanRole(u.role)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label text-xs">
              Unblinded until <span className="text-danger-600">*</span>
            </label>
            <input
              type="datetime-local"
              className="input text-sm"
              value={activeTo}
              onChange={(e) => setActiveTo(e.target.value)}
            />
            {activeTo && !expiryInFuture && (
              <p className="mt-1 text-[11px] text-danger-600">Pick a time in the future.</p>
            )}
            <p className="mt-1 text-[11px] text-ink-muted">
              The designation stops applying at this instant. You can also end it early with
              the End action on the row.
            </p>
          </div>
          <div>
            <label className="label text-xs">
              Reason <span className="text-danger-600">*</span>
            </label>
            <textarea
              className="input text-sm"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={4000}
              placeholder="e.g. SAE on subject 004 — PI needs allocation to direct emergency treatment."
            />
            {reason.trim().length > 0 && reason.trim().length < 10 && (
              <p className="mt-1 text-[11px] text-danger-600">
                At least 10 characters — this is the audit record for an unblinding.
              </p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-surface-border px-5 py-3">
          <button
            type="button"
            className="rounded border border-surface-border px-3 py-1.5 text-sm font-medium text-ink-primary hover:bg-surface-muted/50 disabled:opacity-60"
            onClick={onClose}
            disabled={grantM.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded bg-danger-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-danger-700 disabled:opacity-60"
            onClick={() => grantM.mutate()}
            disabled={!canSubmit}
          >
            {grantM.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Grant emergency unblinding
          </button>
        </div>
      </div>
    </div>
  );
}
